/**
 * React context for json-render components in chat messages.
 * Provides interactivity state so custom registry components know
 * whether to render as interactive or read-only.
 */

'use client'

import { createContext, useContext, type ReactNode } from 'react'

interface ChatRendererContextValue {
  /** Whether this is the most recent assistant message */
  isLastMessage: boolean
  /** Text from the next user message (for restoring submitted state) */
  submittedText?: string
  /** Whether the component should accept user interaction */
  isInteractive: boolean
  /** Sandbox ID for file operations (downloads, previews) */
  sandboxId?: string
  /** Sends a chat message on behalf of the user (for clickable options) */
  sendMessage?: (text: string) => void
}

const ChatRendererContext = createContext<ChatRendererContextValue>({
  isLastMessage: false,
  submittedText: undefined,
  isInteractive: false,
  sandboxId: undefined,
  sendMessage: undefined,
})

interface ChatRendererProviderProps {
  isLastMessage: boolean
  submittedText?: string
  sandboxId?: string
  sendMessage?: (text: string) => void
  children: ReactNode
}

/**
 * Wraps json-render Renderer in chat to provide interactivity context.
 * Components check isInteractive to decide whether to accept clicks/input.
 */
export function ChatRendererProvider({
  isLastMessage,
  submittedText,
  sandboxId,
  sendMessage,
  children,
}: ChatRendererProviderProps) {
  return (
    <ChatRendererContext.Provider
      value={{
        isLastMessage,
        submittedText,
        isInteractive: isLastMessage && !submittedText,
        sandboxId,
        sendMessage,
      }}
    >
      {children}
    </ChatRendererContext.Provider>
  )
}

/**
 * Hook to access chat renderer context from custom json-render components
 */
export function useChatRenderer() {
  return useContext(ChatRendererContext)
}
